// Other plugins.
import { logManager } from '../../logs'
import { logLevels } from 'kandy-logger'

// Libraries.
import sdpTransform from 'sdp-transform'
import { cloneDeep, compose, isArray, isFunction, join, map, trimCharsStart, trimEnd } from 'lodash/fp'
import diff from 'variable-diff'

const log = logManager.getLogger('SDP')

/**
 * Basic SDP pipeline runner that logs the functions in Yaml.
 * @method sdpPipeline
 * @param  {Array}      handlers       List of functions that transform the SDP.
 * @param  {string}     sdp            The session description.
 * @param  {Object}     info           Information about the session description.
 * @param  {string}     info.callId    The id corresponding to the call on which this pipeline is being run.
 * @param  {RTCSdpType} info.type      The session description's type.
 * @param  {string}     info.step      The step that will occur after the Pipeline is run.
 *    Will be either 'set' (the SDP will be set locally) or 'send' (the SDP will be sent
 *    to the remote endpoint).
 * @param  {string}     info.endpoint  Which end of the connection created the SDP.
 * @param  {BandwidthControls} [info.bandwidth] Information about bandwidth controls.
 * @return {string}     The modified session description.
 */
export default function runPipeline (handlers, sdp, info) {
  const logLevel = log.getLevel()
  const isDebug = logLevel === logLevels.DEBUG || logLevel === logLevels.TRACE
  const pipelineFunc = isDebug ? runPipelineYaml : runPipelineDefault

  return pipelineFunc(handlers, sdp, info)
}

function runPipelineDefault (handlers, sdp, info) {
  const objectSdp = sdpTransform.parse(sdp)

  const originalSdp = Object.freeze(objectSdp)
  let newSdp = cloneDeep(originalSdp)

  if (isArray(handlers)) {
    handlers.forEach(handler => {
      const name = formattedFunctionName(handler)

      if (isFunction(handler)) {
        try {
          newSdp = handler(newSdp, info, originalSdp)
        } catch (error) {
          log.info(
            `Currently running pipeline to ${info.step} ${info.endpoint} ${info.type} for callId: ${info.callId}`
          )
          log.error(`Error running SDP handler: ${name}`, error)
        }
      } else {
        log.error('SDP handler not a function; skipping.')
      }
    })
  }

  return sdpTransform.write(newSdp)
}

function runPipelineYaml (handlers, sdp, info) {
  const objectSdp = sdpTransform.parse(sdp)

  if (!isArray(handlers)) {
    return sdpTransform.write(objectSdp)
  }

  const originalSdp = Object.freeze(objectSdp)
  let newSdp = cloneDeep(originalSdp)

  /**
   * For each sdpHandler function we want to log the changes
   * it makes to the SDP and present a yaml report of the diffs.
   * So that each diff doesn't contain all previous changes
   * we need to diff the sdp at it's current stage of transformation.
   * Only perform this work if we're in debug logging mode.
   */
  let diffed
  let oldNewSdp
  const results = []

  handlers.forEach(handler => {
    const name = formattedFunctionName(handler)

    if (isFunction(handler)) {
      log.debug(`Running SDP handler ${name}.`)

      try {
        oldNewSdp = cloneDeep(newSdp)
        newSdp = handler(newSdp, info, originalSdp)
        diffed = diff(oldNewSdp, newSdp)
        results.push({ name, diff: diffed.text })
      } catch (error) {
        log.info(`Currently running pipeline to ${info.step} ${info.endpoint} ${info.type} for callId: ${info.callId}`)
        log.error(`Error running SDP handler: ${name}`, error)
      }
    } else {
      log.error('SDP handler not a function; skipping.')
      results.push({ name, diff: 'No diff, not a function.' })
    }
  })

  diffed = diff(originalSdp, newSdp)
  const yamlFormat = sdpToYaml(info)(originalSdp)(newSdp)(results)(diffed)
  log.debug(`SDP Handler changes: ${yamlFormat}`)

  return sdpTransform.write(newSdp)
}

const sdpToYaml = info => originalSdp => newSdp => results => finalDiffObject =>
  `
---
sdp-pipeline-run:
  info:
    callId: ${info.callId}
    type: ${info.type}
    step: ${info.step}
    endpoint: ${info.endpoint}
    bandwidth: ${displayBandwidth(info.bandwidth)}
  sdp: |
    ${displaySdp(fourSpaces)(originalSdp)}
  handlers:
    ${displayHandlers(results)}
  final:
    diff: |
      ${displayDiffs(sixSpaces)(finalDiffObject.text)}
    sdp: |
      ${finalDiffObject.changed ? displaySdp(sixSpaces)(newSdp) : 'No changes to the SDP'}
...
`

// Display helper functions
const displayBandwidth = bandwidth =>
  bandwidth &&
  JSON.stringify(bandwidth, null, ' ')
    .replace(/\n/g, ' ')
    .replace(/ {2}/g, ' ')

const displaySdp = indentation => sdp =>
  sdpTransform
    .write(sdp)
    .replace(/\n/g, `\n${indentation}`)
    .trimEnd()

const handlerToYaml = ({ name, diff }) =>
  // replacing newlines in case the name is the function.
  `${fourSpaces}- name: ${name.replace(/\n/g, '')}
      diff: |
        ${displayDiffs(eightSpaces)(diff)}
`

const displayHandlers = compose([trimEnd, trimCharsStart(' \n'), join(''), map(handlerToYaml)])

const displayDiffs = indentation => diffed => (diffed ? diffed.replace(/\n/g, `\n${indentation}`) : 'No changes found')

// If the function is anonymous give the function body as the 'name'
const formattedFunctionName = handler =>
  handler.name ||
  handler
    .toString()
    .slice(0, 120)
    .replace(/\n/g, '')

// Space constants
const fourSpaces = '    '
const sixSpaces = '      '
const eightSpaces = '        '
